/**
 * CohortMentorView — Cohort detail page for mentors.
 *
 * Tabs: Overview → My Mentees → Activities → Participants → Metrics
 */
import SpaceBetween from '@cloudscape-design/components/space-between';
import Box from '@cloudscape-design/components/box';
import Tabs from '@cloudscape-design/components/tabs';
import { useHashTab } from '../../hooks/useHashTab';
import { Cohort } from '../../api/cohorts';
import { ProgramAdmin } from '../../api/programs';
import { useQuery } from '@tanstack/react-query';
import { mentorsApi } from '../../api/mentors';
import { activitiesApi } from '../../api/activities';
import { cohortsApi } from '../../api/cohorts';
import { queryKeys } from '../../lib/queryClient';
import { useAuthStore } from '../../stores/authStore';
import { CohortOverviewTab } from '../cohort-tabs/CohortOverviewTab';
import { MyMenteesTab } from '../cohort-tabs/MyMenteesTab';
import { ActivitiesTab } from '../cohort-tabs/ActivitiesTab';
import { ParticipantsTab } from '../cohort-tabs/ParticipantsTab';

interface CohortMentorViewProps {
  cohort: Cohort;
  programAdmins: ProgramAdmin[];
  programWikiUrl?: string;
  programName?: string;
  programOwnerId?: string;
  programOwnerName?: string;
}

export function CohortMentorView({ cohort, programAdmins, programWikiUrl, programName, programOwnerId, programOwnerName }: CohortMentorViewProps) {
  const [activeTabId, setActiveTabId] = useHashTab('overview');
  const user = useAuthStore((state) => state.user);

  const { data: menteesData } = useQuery({
    queryKey: queryKeys.mentors.mentees(cohort.cohortId, user?.userId ?? ''),
    queryFn: () => mentorsApi.getMentees(cohort.cohortId, user!.userId),
    enabled: !!user?.userId,
  });

  const { data: activities } = useQuery({
    queryKey: queryKeys.activities.list(cohort.cohortId),
    queryFn: () => activitiesApi.list(cohort.cohortId),
  });
  const activityCount = activities?.length ?? 0;

  const { data: participantsData } = useQuery({
    queryKey: queryKeys.cohorts.participants(cohort.cohortId),
    queryFn: () => cohortsApi.getParticipants(cohort.cohortId),
  });
  const participantCount = participantsData?.participantCount;

  const tabs = [
    {
      id: 'overview',
      label: 'Overview',
      content: (
        <CohortOverviewTab
          cohort={cohort}
          programAdmins={programAdmins}
          programWikiUrl={programWikiUrl}
          programOwnerId={programOwnerId}
          programOwnerName={programOwnerName}
          onNavigateToTab={setActiveTabId}
        />
      ),
    },
    {
      id: 'my-mentees',
      label: `My Mentees (${menteesData?.length ?? 0})`,
      content: <MyMenteesTab cohortId={cohort.cohortId} mentorId={user?.userId ?? ''} />,
    },
    {
      id: 'activities',
      label: `Activities (${activityCount})`,
      content: (
        <ActivitiesTab
          cohortId={cohort.cohortId}
          programId={cohort.programId}
          mentorsEnabled={cohort.mentorsEnabled}
          cohortStartDate={cohort.startDate}
          cohortEndDate={cohort.endDate}
        />
      ),
    },
    {
      id: 'participants',
      label: `Participants${participantCount !== undefined ? ` (${participantCount}${cohort.capacity ? `/${cohort.capacity}` : ''})` : ''}`,
      content: (
        <ParticipantsTab
          cohortId={cohort.cohortId}
          programId={cohort.programId}
          mentorsEnabled={cohort.mentorsEnabled}
        />
      ),
    },
  ];

  return (
    <SpaceBetween size="m">
      <SpaceBetween size="xxs">
        <Box variant="h1">{programName ?? cohort.cohortName}</Box>
        {programName && <Box variant="h2" color="text-body-secondary">{cohort.cohortName}</Box>}
      </SpaceBetween>
      <Tabs activeTabId={activeTabId} onChange={({ detail }) => setActiveTabId(detail.activeTabId)} tabs={tabs} />
    </SpaceBetween>
  );
}
